import 'bootstrap/dist/css/bootstrap.min.css';

import './App.css';
import './Global.css';

import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { supabase, getCachedSession, clearSupabaseAuthStorage } from './supabaseClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// import LandingPage from './UI/LandingPage';
import AuthPage from './UI/Components/Auth/AuthPage';
import ResetPasswordPage from './UI/Components/Auth/ResetPasswordPage';
import WebsiteScanner from './UI/WebsiteScanner';
import History from './UI/History';
import Account from './UI/Account';
import MasterAdmin from "./UI/MasterAdmin";
import GoogleCallback from './UI/GoogleCallback';

import PricingPlans from './UI/Components/PricingPlans';
import SharedReportView from './UI/SharedReportView';
import Dashboard from './UI/Dashboard';
import UserDashboard from './UI/UserDashboard';


function App() {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    let intervalId;

    const checkSessionExpiry = () => {
      // Don't interrupt users on the reset password page
      if (window.location.pathname === '/reset-password') {
        setShowWarning(false);
        return;
      }

      const session = getCachedSession();
      if (!session) {
        setShowWarning(false);
        return;
      }

      const expiresAt = session.expires_at; // unix timestamp in seconds
      const currentTime = Math.floor(Date.now() / 1000);
      const secondsRemaining = expiresAt - currentTime;

      if (secondsRemaining <= 0) {
        // Session expired, clear storage and redirect
        clearSupabaseAuthStorage();
        window.location.href = "/auth";
      } else if (secondsRemaining < 300) {
        // Less than 5 minutes remaining
        setTimeLeft(Math.ceil(secondsRemaining / 60));
        setShowWarning(true);
      } else {
        // Session is healthy
        setShowWarning(false);
      }
    };

    // Run check on mount
    checkSessionExpiry();

    // Check every 30 seconds
    intervalId = setInterval(checkSessionExpiry, 30000);

    // Also listen to auth changes to immediately re-check
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      setTimeout(checkSessionExpiry, 0);
    });

    return () => {
      clearInterval(intervalId);
      subscription?.unsubscribe();
    };
  }, []);

  const handleExtendSession = async () => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;
      setShowWarning(false);
    } catch (err) {
      console.error("Failed to extend session:", err);
      toast.error("Could not extend session. Please sign in again.");
      window.location.href = "/auth";
    }
  };

  return (
    <>
      <Router>
        <Routes>
          <Route 
            path="/" 
            element={<Navigate to="/scanner" replace />}
          />
          
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          <Route path="/scanner" element={<WebsiteScanner />} />

          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/history" element={<History />} /> {/* New route */}
          
          <Route path="/account" element={<Account />} /> {/* New route */}

          <Route path="/user-dashboard" element={<UserDashboard />} />

          <Route path= "/master-admin" element = {<MasterAdmin />} /> {/* New route */}

          <Route path="/google-callback" element={<GoogleCallback />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/pricing" element={<PricingPlans />} />
          <Route path="/reports/shared/:shareToken" element={<SharedReportView />} />
          <Route path="/report/shared/:shareToken" element={<SharedReportView />} />
        </Routes>
      </Router>

      <Modal show={showWarning} backdrop="static" keyboard={false} centered>
        <Modal.Header className="bg-warning text-dark">
          <Modal.Title>Session Expiration Warning ⚠️</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-dark">
          Your session will expire in approximately <strong>{timeLeft} minute{timeLeft !== 1 ? 's' : ''}</strong> due to inactivity. Would you like to stay signed in?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={async () => {
            await supabase.auth.signOut();
            window.location.href = "/auth";
          }}>
            Log Out
          </Button>
          <Button variant="primary" onClick={handleExtendSession}>
            Extend Session
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="bottom-right" autoClose={4000} theme="dark" />
    </>
  );
}

export default App;
